import { useCallback, useState } from "react";
import { getQuestions, getRefinedRecommendations } from "../api/recommendations";
import { useRecoEngine } from "../context/RecoEngineContext";
import ChatBlock from "../components/ChatBlock";
import ServicesRow from "../components/ServicesRow";
import { sendAnalyticsClickEvent } from "../../../utils/CommonFunctions";
import RefineForm from "../components/RefineForm";

interface UseRefineFormProps {
    setRefineFormData?: (data: any) => void;
    addBlock?: (block: any) => void;
  }

const useRefineForm = ({ setRefineFormData, addBlock }: UseRefineFormProps) => {
    const {
        queryObject,
        setLoading,
        servicesObject,
        setServicesObject,
        setRenderBlocks,
        setFeedbackRow,
        questionsObject,
        setQuestionsObject,
        setRefineForm
      } = useRecoEngine();

      const [staticQuestions, setStaticQuestions] = useState({questions: [], answers: []});
      const [err, setErr] = useState(false);
  
    const prepareFormData = useCallback(() => ({
        query: queryObject,
        questions: questionsObject.questions,
        answers: questionsObject.answers,
        recommendation: servicesObject?.message.replace(/\s+/g, " ").slice(0, 100),
      }), [queryObject, questionsObject, servicesObject?.message]);
  
    const storeFormInSession = useCallback(() => {
      setRefineFormData?.({
        questions: questionsObject.questions,
        answers: questionsObject.answers,
        isActive: false
      });
    }, [questionsObject, setRefineFormData]);
  
    const addStorageBlock = useCallback(() => {
      addBlock?.({
        type: "refine",
        data: {
          questions: getAnsweredQuestions(),
          answers: getAnsweredAnswers()
        }
      });
    }, [questionsObject, addBlock]);

    const getAnsweredQuestions = useCallback(() => questionsObject.questions.filter((_, index) => questionsObject.answers?.[index] && questionsObject.answers[index].trim() !== ""), [questionsObject]);

    const getAnsweredAnswers = useCallback(() => questionsObject.answers.filter((answer) => answer && answer.trim() !== ""), [questionsObject]);
  
    const renderStorageBlock = useCallback(() => {
      setRenderBlocks((prevBlocks) => [
        ...prevBlocks,
        <RefineForm
          key={`refine-${Date.now()}`}
          mode="display"
          questions={getAnsweredQuestions()}
          answers={getAnsweredAnswers()}
        />
      ]);
    }, [staticQuestions, setRenderBlocks]);
  
    const addResponseBlocksToSession = useCallback((response: any) => {
      addBlock?.({
        type: "chat",
        data: {
          text: response?.message,
          engineResponse: true
        }
      });
      
      addBlock?.({
        type: "services",
        data: {
          services: response.services
        }
      });
      
      addBlock?.({
        type: "feedback",
        data: {
          options: ["end", "refine", "new"]
        }
      });
    }, [addBlock]);
  
    const renderResponseBlocks = useCallback((response: any) => {
      setRenderBlocks((prevBlocks) => [
        ...prevBlocks,
        <ChatBlock key={`chat-${Date.now()}`} text={response?.message} engineResponse />,
        <ServicesRow key={`services-${Date.now()}`} services={response.services} />,
      ]);
    }, [setRenderBlocks]);

    const fetchQuestions = useCallback(async () => {
        setLoading(true);

        if (questionsObject?.questions.length > 0) {
          return;
        }
        
        try {
          const response = await getQuestions(servicesObject?.message, queryObject.query);
          setQuestionsObject({
            ...questionsObject,
            questions: response.questions,
            answers: []
          });
        } catch (error) {
          console.error("Error fetching recommendations", error);
        } finally {
          setLoading(false);
        }
      }, [questionsObject, servicesObject?.message, queryObject.query, setLoading]);
  
    const handleSubmit = useCallback(async () => {
      try {
        // Check if all answers are filled
        const answeredAnswers = getAnsweredAnswers();
        
        if (answeredAnswers.length === 0) {
          setErr(true);
          return; // Don't submit if no answers
        }
        // Prepare and store form data
        const payload = prepareFormData();
        storeFormInSession();
        addStorageBlock();
        renderStorageBlock();
        
        // Hide the form
        setLoading(true);
        setRefineForm(false);
        
        // Get refined recommendations
        const response = await getRefinedRecommendations(payload);
        setServicesObject(response);

        setQuestionsObject({
            questions: [],
            answers: []
        });

        setStaticQuestions({
            questions: [],
            answers: []
        });
        
        // Add blocks to session and render them
        addResponseBlocksToSession(response);
        renderResponseBlocks(response);
        
        // Show feedback options
        setFeedbackRow(true);
        
        // Send analytics
        sendAnalyticsClickEvent("Refine Form Submit", "RECO_ENGINE");
        
      } catch (error) {
        console.error("Error in form submission:", error);
        // Handle error appropriately
      } finally {
        setLoading(false);
      }
    }, [
      prepareFormData,
      storeFormInSession,
      addStorageBlock,
      renderStorageBlock,
      setRefineForm,
      setServicesObject,
      addResponseBlocksToSession,
      renderResponseBlocks,
      setFeedbackRow
    ]);

    const updateAnswer = useCallback((index: number, value: string) => {
        setErr(false);
        const updatedAnswers = [...(questionsObject.answers || [])];
    
        while (updatedAnswers.length <= index) {
          updatedAnswers.push("");
        }
    
        updatedAnswers[index] = value;
    
        setQuestionsObject({
          ...questionsObject,
          answers: updatedAnswers,
        });
        
        setStaticQuestions({
          questions: questionsObject.questions,
          answers: updatedAnswers,
        });
      }, [questionsObject, setQuestionsObject]);
  
    return {
        staticQuestions,
        err,
        questionsObject,
        fetchQuestions,
        handleSubmit,
        updateAnswer,
        setErr
    };
  };

export default useRefineForm;